// Developer affordance — a sliding right-side drawer that shows every
// middleware HTTP call made by the widget in real time. Useful during
// integration debugging and live demos.
//
// Brought forward from the POC's TRAFFIC panel; same event taxonomy
// (`cc-api-traffic`) so any future GA4-event instrumentation can layer
// in without changes here.

import { useEffect, useRef, useState } from 'react';
import type { TrafficEntry } from '../api/client';

const MAX_ENTRIES = 100;

export function TrafficDrawer() {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<TrafficEntry[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<TrafficEntry>;
      setEntries((prev) => {
        const next = [...prev, ce.detail];
        if (next.length > MAX_ENTRIES) next.shift();
        return next;
      });
    };
    window.addEventListener('cc-api-traffic', handler);
    return () => window.removeEventListener('cc-api-traffic', handler);
  }, []);

  // Auto-scroll on new entries when drawer is open
  useEffect(() => {
    if (open && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [entries, open]);

  return (
    <>
      <button
        aria-label={open ? 'Close API traffic drawer' : 'Open API traffic drawer'}
        title="API traffic"
        onClick={() => setOpen((v) => !v)}
        style={floatingButtonStyle(open)}
      >
        {open ? '×' : '</>'}
      </button>

      <aside style={drawerStyle(open)} role="complementary" aria-label="API traffic">
        <div style={headerStyle}>
          <strong style={{ flex: 1 }}>API TRAFFIC</strong>
          <span style={{ fontSize: 11, color: '#888', marginRight: 12 }}>
            {entries.length} / {MAX_ENTRIES}
          </span>
          <button onClick={() => setEntries([])} style={drawerButtonStyle}>Clear</button>
          <button onClick={() => setOpen(false)} style={drawerButtonStyle}>Close</button>
        </div>

        <div ref={listRef} style={listStyle}>
          {entries.length === 0 ? (
            <div style={emptyStyle}>No traffic yet. Engage the widget to see middleware calls.</div>
          ) : (
            entries.map((e) => (
              <div key={e.id} style={entryStyle(e.ok)}>
                <div
                  style={entryHeaderStyle}
                  onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                >
                  <span style={methodPillStyle(e.method)}>{e.method}</span>
                  <span style={statusPillStyle(e.status, e.ok)}>
                    {e.status === 0 ? 'ERR' : e.status}
                  </span>
                  <span style={pathStyle}>{e.path}</span>
                  <span style={durationStyle}>{e.durationMs}ms</span>
                </div>
                {expanded === e.id && (
                  <div style={detailStyle}>
                    <div style={timestampStyle}>
                      {new Date(e.timestamp).toLocaleTimeString()}
                    </div>
                    {e.requestBody && (
                      <>
                        <div style={detailLabelStyle}>Request</div>
                        <pre style={preStyle}>{truncate(e.requestBody, 2000)}</pre>
                      </>
                    )}
                    {e.responseBody && (
                      <>
                        <div style={detailLabelStyle}>Response</div>
                        <pre style={preStyle}>{tryPretty(truncate(e.responseBody, 4000))}</pre>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </aside>
    </>
  );
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n) + `… (${s.length - n} more chars)`;
}

function tryPretty(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

// ─── Inline styles (drawer is fixed-position; using inline styles keeps it
// independent of the widget's CSS module hashing) ──────────────────────────

const floatingButtonStyle = (open: boolean): React.CSSProperties => ({
  position: 'fixed',
  bottom: 16,
  right: 16,
  width: 44,
  height: 44,
  borderRadius: '50%',
  background: open ? '#666' : '#1a1a1a',
  color: '#fff',
  border: 'none',
  fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
  fontSize: open ? 22 : 14,
  fontWeight: 600,
  cursor: 'pointer',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
  zIndex: 2147483646,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
});

const drawerStyle = (open: boolean): React.CSSProperties => ({
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  width: 'min(480px, 90vw)',
  background: '#1a1a1a',
  color: '#eee',
  fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
  fontSize: 12,
  transform: open ? 'translateX(0)' : 'translateX(100%)',
  transition: 'transform 0.25s ease-out',
  zIndex: 2147483645,
  display: 'flex',
  flexDirection: 'column',
  boxShadow: open ? '-4px 0 24px rgba(0, 0, 0, 0.3)' : 'none',
});

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '12px 16px',
  borderBottom: '1px solid #333',
  fontSize: 12,
  letterSpacing: '0.5px',
  gap: 8,
};

const drawerButtonStyle: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid #555',
  color: '#ccc',
  padding: '4px 10px',
  borderRadius: 4,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 11,
  marginLeft: 4,
};

const listStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '8px 0',
};

const emptyStyle: React.CSSProperties = {
  padding: 32,
  textAlign: 'center',
  color: '#888',
  fontStyle: 'italic',
};

const entryStyle = (ok: boolean): React.CSSProperties => ({
  borderLeft: `3px solid ${ok ? '#2a7a3e' : '#c53030'}`,
  background: '#222',
  margin: '4px 8px',
  borderRadius: 4,
});

const entryHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  cursor: 'pointer',
};

const methodPillStyle = (method: string): React.CSSProperties => ({
  background:
    method === 'GET'
      ? '#3b82f6'
      : method === 'POST'
        ? '#16a34a'
        : method === 'DELETE'
          ? '#ef4444'
          : '#555',
  color: '#fff',
  padding: '2px 6px',
  borderRadius: 3,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.5px',
  minWidth: 50,
  textAlign: 'center',
});

const statusPillStyle = (_status: number, ok: boolean): React.CSSProperties => ({
  background: ok ? '#1a3a52' : '#5c1a1a',
  color: '#fff',
  padding: '2px 6px',
  borderRadius: 3,
  fontSize: 10,
  fontWeight: 700,
  minWidth: 36,
  textAlign: 'center',
});

const pathStyle: React.CSSProperties = {
  flex: 1,
  color: '#ddd',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const durationStyle: React.CSSProperties = {
  color: '#888',
  fontSize: 11,
};

const detailStyle: React.CSSProperties = {
  padding: '0 12px 12px',
  borderTop: '1px dashed #333',
};

const timestampStyle: React.CSSProperties = {
  fontSize: 10,
  color: '#888',
  padding: '8px 0 4px',
};

const detailLabelStyle: React.CSSProperties = {
  fontSize: 10,
  color: '#888',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginTop: 8,
  marginBottom: 4,
};

const preStyle: React.CSSProperties = {
  background: '#0e0e0e',
  border: '1px solid #333',
  borderRadius: 3,
  padding: 8,
  margin: 0,
  fontSize: 11,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  color: '#bbb',
  maxHeight: 240,
  overflowY: 'auto',
};
